import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./WebChatWidget.css";

/**
 * Widget de chat interno de DIM (messaging-gateway del CRM).
 *
 * El gateway sirve el HTML completo del chat por apiKey y se embebe en un iframe.
 * El host (esta app) solo decide como mostrarlo y habla con el iframe por postMessage.
 * Se monta UNA sola vez a nivel app (ver WebChatProvider); los botones de entrada
 * (pestaña del login, boton flotante logueado) solo abren/cierran via contexto.
 *
 * Contrato postMessage (source 'dim-webchat' = widget, 'dim-webchat-host' = host):
 *   widget -> host : webchat:ready | webchat:minimize | webchat:unread {count}
 *                    | webchat:expand | webchat:restore
 *   host -> widget : webchat:shown | webchat:hidden | webchat:expanded
 *                    | webchat:portal-jwt {jwt} | webchat:reset-session
 */

const HOST_SOURCE = "dim-webchat-host";
const WIDGET_SOURCE = "dim-webchat";

/**
 * Lee la config del widget desde config.json (persistido en localStorage.config).
 * Devuelve null si el chat no esta configurado (sin url o sin apiKey).
 */
export const getWebChatConfig = () => {
  try {
    const config = JSON.parse(localStorage.getItem("config") || "{}");
    const webchat = config?.webchat;
    if (!webchat || !webchat.url || !webchat.apiKey) return null;
    const origin = new URL(webchat.url).origin;
    // hostOrigin: en iOS la app corre en capacitor:// y WebKit no manda Origin
    // ni Referer al cargar el iframe desde ese esquema; sin ellos el gateway
    // responde 404. Con este parametro valida el origen contra los permitidos
    // de la apiKey. En web y Android lo ignora porque ya viene el Referer.
    return {
      origin,
      src: `${origin}/webchat/plugin?apiKey=${encodeURIComponent(
        webchat.apiKey
      )}&hostOrigin=${encodeURIComponent(window.location.origin)}`,
    };
  } catch (_e) {
    return null;
  }
};

/**
 * @param open          panel visible
 * @param onClose       el widget pidio minimizarse (o se cerro la sesion)
 * @param onUnread      contador de no leidos (con el panel abierto o cerrado)
 * @param webchat       { origin, src } de getWebChatConfig()
 * @param authenticated hay sesion del Portal: se inyecta sessionStorage.token al widget
 * @param raised        el panel se despega del borde inferior para dejar lugar al boton flotante
 */
const WebChatWidget = ({
  open,
  onClose,
  onUnread,
  webchat,
  authenticated = false,
  raised = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const frameRef = useRef(null);
  const openRef = useRef(open);
  const jwtEnviadoRef = useRef(false);
  const authenticatedRef = useRef(authenticated);
  const retryTimerRef = useRef(null);
  const resetTimerRef = useRef(null);

  const postToWidget = useCallback(
    (type, payload) => {
      const win = frameRef.current?.contentWindow;
      if (!win) return;
      const msg = { source: HOST_SOURCE, type };
      if (payload !== undefined) msg.payload = payload;
      // Origen explicito, nunca '*': el widget descarta el JWT si no viene del origen registrado.
      win.postMessage(msg, webchat.origin);
    },
    [webchat.origin]
  );

  // Si el paciente esta logueado le pasamos el JWT del Portal para saltear DNI + OTP.
  // Se manda una sola vez por carga del iframe (el widget tambien lo deduplica);
  // reenviar=true saltea el dedupe local para el reintento post-load.
  const enviarSesionPortal = useCallback(
    (reenviar = false) => {
      if (jwtEnviadoRef.current && !reenviar) return;
      const jwt = sessionStorage.token;
      if (!jwt) return;
      jwtEnviadoRef.current = true;
      postToWidget("webchat:portal-jwt", { jwt });
    },
    [postToWidget]
  );

  // El JWT recien puede viajar cuando el iframe termino de navegar al gateway:
  // antes del load el frame esta en about:blank y el postMessage dirigido al
  // origen del widget se descarta en silencio. Reintento a 1.5s por si el
  // widget carga pero registra su listener despues del load.
  const onFrameLoad = useCallback(() => {
    jwtEnviadoRef.current = false;
    enviarSesionPortal();
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => enviarSesionPortal(true), 1500);
  }, [enviarSesionPortal]);

  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    []
  );

  // El JWT dura una hora: cuando axiosLogin lo renueva en silencio (ante un 401)
  // hay que reinyectarlo o la conversacion larga vuelve a pedir OTP. El widget
  // deduplica por valor, asi que reenviar de mas no genera llamadas.
  useEffect(() => {
    if (!mounted) return undefined;
    const onJwtRenovado = () => enviarSesionPortal(true);
    window.addEventListener("portal:jwt-renovado", onJwtRenovado);
    return () => window.removeEventListener("portal:jwt-renovado", onJwtRenovado);
  }, [mounted, enviarSesionPortal]);

  // Montaje diferido: el iframe (y su conexion SSE) recien se crea la primera vez que se abre.
  // Despues queda montado aunque se cierre, para conservar la conversacion y contar no leidos.
  useEffect(() => {
    if (open && !mounted) setMounted(true);
  }, [open, mounted]);

  // Avisar al widget cada vez que el host lo muestra u oculta.
  useEffect(() => {
    openRef.current = open;
    if (!mounted) return;
    if (open) {
      postToWidget("webchat:shown");
      if (onUnread) onUnread(0);
      // El JWT NO se manda aca: en la primera apertura el iframe todavia no
      // navego al gateway y el mensaje se perderia. Lo maneja onFrameLoad.
    } else {
      setExpanded(false);
      postToWidget("webchat:hidden");
    }
  }, [open, mounted, postToWidget, onUnread]);

  // Cambios de sesion del Portal:
  //  - login con el chat ya montado: inyectar el JWT aunque el panel este cerrado
  //    (por ejemplo si el bot estaba pidiendo DNI).
  //  - logout: avisar webchat:reset-session y recien despues desmontar el iframe.
  //    El widget necesita ese instante para borrar el sessionId guardado en el
  //    navegador: si el iframe muere en el mismo momento, en una PC compartida
  //    el proximo usuario veria la conversacion del paciente anterior.
  useEffect(() => {
    const antes = authenticatedRef.current;
    authenticatedRef.current = authenticated;
    if (antes === authenticated || !mounted) return;
    if (authenticated) {
      // Login antes de que venza la espera del logout: cancelar el desmonte.
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
      enviarSesionPortal();
    } else {
      jwtEnviadoRef.current = false;
      setExpanded(false);
      postToWidget("webchat:reset-session");
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        resetTimerRef.current = null;
        setMounted(false);
        if (onClose) onClose();
      }, 150);
    }
  }, [authenticated, mounted, enviarSesionPortal, postToWidget, onClose]);

  // Mensajes del widget hacia el host.
  useEffect(() => {
    if (!mounted) return;
    const onMessage = (e) => {
      if (e.origin !== webchat.origin) return;
      if (!e.data || e.data.source !== WIDGET_SOURCE) return;
      switch (e.data.type) {
        case "webchat:ready":
          // (Re)carga del iframe: el widget arranca creyendose visible y sin sesion.
          jwtEnviadoRef.current = false;
          if (!openRef.current) postToWidget("webchat:hidden");
          enviarSesionPortal(); // no hace nada si no hay sessionStorage.token
          break;
        case "webchat:minimize":
          if (onClose) onClose();
          break;
        case "webchat:unread":
          // Tambien con el panel abierto: el widget lo emite cuando el paciente
          // no esta atendiendo el chat y manda {count: 0} solo cuando lo lee.
          if (onUnread)
            onUnread((e.data.payload && e.data.payload.count) || 0);
          break;
        case "webchat:expand":
          // Hay que responder el acuse: si no, a los 300ms el widget asume que el host
          // no sabe agrandarse y los formularios modales quedan apretados.
          setExpanded(true);
          postToWidget("webchat:expanded");
          break;
        case "webchat:restore":
          setExpanded(false);
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [mounted, webchat.origin, postToWidget, enviarSesionPortal, onClose, onUnread]);

  if (!mounted) return null;

  const className = [
    "webchat-panel",
    open ? "webchat-panel--open" : "",
    expanded ? "webchat-panel--expanded" : "",
    raised ? "webchat-panel--raised" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Portal a body: el login esta dentro de contenedores con transform (framer-motion),
  // y un position:fixed adentro de un transform se posiciona relativo a ese ancestro.
  return ReactDOM.createPortal(
    <div className={className} aria-hidden={!open}>
      <iframe
        ref={frameRef}
        title="Chat de ayuda DIM"
        src={webchat.src}
        onLoad={onFrameLoad}
        allow="clipboard-write"
      />
    </div>,
    document.body
  );
};

export default WebChatWidget;
